/**
 * Alert lifecycle telemetry (ENG-4874).
 *
 * Every alert from the "Tell people when something breaks" initiative records
 * three moments of one showing: shown, closed and acted on. The three rows
 * share a `showing_id`, minted per display by the ENG-4930 incident helper,
 * so Product can follow one problem from the moment it appeared to what the
 * person did about it. In-place messages (N1, N3, N7, N11) use the same
 * events: in place is a display choice, not a different event.
 *
 * Person, product and environment are attached by the context resolver;
 * `studio_id` is stamped whenever a studio is active, and callers pass
 * `agent_id` for the Bridge where one is involved.
 *
 * Same injected-`track` shape as the sibling trackers (auth-journey.ts) so
 * the emit-site scanner follows the sends and a unit test needs no module mock.
 * Per rule: reference-rules-in-comments (ai.knowledge/global); contract:
 * ENG-4874 "Event contract (Oct 28)".
 */

import type { EventCategory } from '../constants/event-categories.js';
import type { EventName } from '../constants/event-names.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import type { AnalyticsTrackFn } from './types.js';

/** `alert_id`: one value per N-ticket. */
export const ALERT_ID = {
  N1: 'n1',
  N2: 'n2',
  N3: 'n3',
  N4: 'n4',
  N5: 'n5',
  N6: 'n6',
  N7: 'n7',
  N8: 'n8',
  N9: 'n9',
  N10: 'n10',
  N11: 'n11',
  N12: 'n12'
} as const;

export type AlertId = (typeof ALERT_ID)[keyof typeof ALERT_ID];

/**
 * `alert_variant`: only where a ticket has versions. N5 recording /
 * not_recording. N6 and N10 local / remote. N3 studios / organizations. N7 the
 * reason the camera could not be reached. N11 the failed discovery method,
 * mdns / scan (ENG-4868 Rules settled; ENG-4948 DiscoveryMethod).
 */
export const ALERT_VARIANT = {
  RECORDING: 'recording',
  NOT_RECORDING: 'not_recording',
  LOCAL: 'local',
  REMOTE: 'remote',
  STUDIOS: 'studios',
  ORGANIZATIONS: 'organizations',
  NO_RESPONSE: 'no_response',
  NAME_NOT_FOUND: 'name_not_found',
  REFUSED: 'refused',
  NOT_RTSP: 'not_rtsp',
  GENERAL: 'general',
  MDNS: 'mdns',
  SCAN: 'scan'
} as const;

export type AlertVariant = (typeof ALERT_VARIANT)[keyof typeof ALERT_VARIANT];

/** `close_reason` on `alert_closed`. */
export const ALERT_CLOSE_REASON = {
  /** The X. */
  DISMISS: 'dismiss',
  /** Green auto-close. */
  TIMEOUT: 'timeout',
  /** The problem went away. */
  RESOLVED: 'resolved',
  /** Replaced, for example by the session pop-up. */
  SUPERSEDED: 'superseded'
} as const;

export type AlertCloseReason = (typeof ALERT_CLOSE_REASON)[keyof typeof ALERT_CLOSE_REASON];

/** `action` on `alert_acted`: the button pressed. */
export const ALERT_ACTION = {
  SIGN_IN: 'sign_in',
  TRY_AGAIN: 'try_again',
  SHOW_IN_FOLDER: 'show_in_folder',
  OPEN_OUTPUT_SETTINGS: 'open_output_settings',
  UPDATE: 'update',
  DOWNLOAD: 'download',
  ADD_BY_ADDRESS: 'add_by_address',
  LEARN_MORE: 'learn_more'
} as const;

export type AlertAction = (typeof ALERT_ACTION)[keyof typeof ALERT_ACTION];

/** `result` on `alert_acted` for `try_again`, once the retry settles. */
export const ALERT_ACTION_RESULT = {
  SUCCESS: 'success',
  FAILURE: 'failure'
} as const;

export type AlertActionResult = (typeof ALERT_ACTION_RESULT)[keyof typeof ALERT_ACTION_RESULT];

/** One showing of one alert: the identity every one of its rows carries. */
export interface AlertShowing {
  alert_id: AlertId;
  /** A fresh UUID per display, identical across shown, closed and acted. */
  showing_id: string;
  alert_variant?: AlertVariant;
  /** Stamped by the context resolver when omitted and a studio is active. */
  studio_id?: string;
  /** The Bridge involved, where one is. */
  agent_id?: string;
  /** N6: how many recordings share this saved-recordings notice. */
  recording_count?: number;
  /** N12: the updater signal's reason code, never its raw error message. */
  reason?: 'updater_absent' | 'attach_failed' | 'non_network_errors';
}

/**
 * `try_again` is the one action with a settled outcome, so it is the one
 * action that must carry `result`; every other action must not.
 */
export type AlertActed =
  | { action: typeof ALERT_ACTION.TRY_AGAIN; result: AlertActionResult }
  | { action: Exclude<AlertAction, typeof ALERT_ACTION.TRY_AGAIN>; result?: never };

// No runtime imports, like sign-out-lifecycle.ts: the incident helper that
// will call this sits under the notifications store, whose jest suites reset
// the registry, and a second concurrent link of event-names.ts trips the
// jest --experimental-vm-modules linker. The literals are checked against the
// catalog types below, so a name missing from EVENT_NAMES still fails tsc.
// Per knowledge: esm-jest-mock-patterns (ai.knowledge/global).
const ALERT_SHOWN_EVENT = 'alert_shown';
const ALERT_CLOSED_EVENT = 'alert_closed';
const ALERT_ACTED_EVENT = 'alert_acted';
const NOTIFICATIONS_CATEGORY = 'notifications';
const assertCatalogued: (name: EventName, category: EventCategory) => void = () => {};
assertCatalogued(ALERT_SHOWN_EVENT, NOTIFICATIONS_CATEGORY);
assertCatalogued(ALERT_CLOSED_EVENT, NOTIFICATIONS_CATEGORY);
assertCatalogued(ALERT_ACTED_EVENT, NOTIFICATIONS_CATEGORY);

// Keys stay absent (never `undefined`) so optional properties do not reach the
// wire as nulls, and so required-property matching sees exactly what was set.
const showingProperties = (showing: AlertShowing): EventProperties => ({
  alert_id: showing.alert_id,
  showing_id: showing.showing_id,
  ...(showing.alert_variant !== undefined ? { alert_variant: showing.alert_variant } : {}),
  ...(showing.studio_id !== undefined ? { studio_id: showing.studio_id } : {}),
  ...(showing.agent_id !== undefined ? { agent_id: showing.agent_id } : {}),
  ...(showing.alert_id === ALERT_ID.N6 && showing.recording_count !== undefined
    ? { recording_count: showing.recording_count }
    : {}),
  ...(showing.alert_id === ALERT_ID.N12 && showing.reason !== undefined
    ? { reason: showing.reason }
    : {})
});

export const createAlertTracker = (track: AnalyticsTrackFn) => ({
  /** The showing appeared on screen. */
  trackAlertShown: (showing: AlertShowing): void => {
    track(ALERT_SHOWN_EVENT, NOTIFICATIONS_CATEGORY, showingProperties(showing));
  },

  /** The showing left the screen, and why. */
  trackAlertClosed: (showing: AlertShowing, close_reason: AlertCloseReason): void => {
    track(ALERT_CLOSED_EVENT, NOTIFICATIONS_CATEGORY, {
      ...showingProperties(showing),
      close_reason
    });
  },

  /**
   * A button on the showing was pressed. For `try_again`, call once the retry
   * settles so the row carries its result.
   */
  trackAlertActed: (showing: AlertShowing, acted: AlertActed): void => {
    track(ALERT_ACTED_EVENT, NOTIFICATIONS_CATEGORY, {
      ...showingProperties(showing),
      action: acted.action,
      ...(acted.result !== undefined ? { result: acted.result } : {})
    });
  }
});

export type AlertTracker = ReturnType<typeof createAlertTracker>;
